import React, { useEffect, useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as firebaseFunctions } from "../../firebase";
import ProWelcomeModal from "../../components/ProWelcomeModal";
import { logAudit } from "../../utils/audit";
import { useFamilyData } from "../../hooks/useFamilyData";
import { useToast } from "../../components/ui/ToastProvider";
import { mapFirebaseError } from "../../utils/userErrors";

export default function SettingsCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { kids = [], tasks = [] } = useFamilyData(familyId, { canManage });
  const [newKidName, setNewKidName] = useState("");
  const [newKidError, setNewKidError] = useState("");
  const [newPin, setNewPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [toddlerPin, setToddlerPin] = useState(familySettings?.toddlerPin || "");
  const [toddlerPinError, setToddlerPinError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saveNotice, setSaveNotice] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [savePending, setSavePending] = useState(0);
  const [showProWelcome, setShowProWelcome] = useState(false);
  const [familyName, setFamilyName] = useState(familySettings?.familyName || "My Family");
  const [autoFailEnabled, setAutoFailEnabled] = useState(familySettings?.autoFailEnabled ?? true);
  const [autoFailGraceMins, setAutoFailGraceMins] = useState(familySettings?.autoFailGraceMins ?? 0);
  const [clarificationsEnabled, setClarificationsEnabled] = useState(
    familySettings?.clarificationsEnabled ?? true
  );
  const [autoApproveDailyEnabled, setAutoApproveDailyEnabled] = useState(
    familySettings?.autoApproveDailyEnabled ?? true
  );
  const [powerHourEnabled, setPowerHourEnabled] = useState(familySettings?.powerHourEnabled || false);
  const [powerHourStart, setPowerHourStart] = useState(familySettings?.powerHourStart || "18:00");
  const [powerHourEnd, setPowerHourEnd] = useState(familySettings?.powerHourEnd || "19:00");
  const [powerHourMultiplier, setPowerHourMultiplier] = useState(familySettings?.powerHourMultiplier || 1.5);
  const [autoFailSaved, setAutoFailSaved] = useState(false);
  const [powerHourSaved, setPowerHourSaved] = useState(false);
  const [powerHourMultiplierSaved, setPowerHourMultiplierSaved] = useState(false);
  const [autoFailSaving, setAutoFailSaving] = useState(false);
  const [powerHourSaving, setPowerHourSaving] = useState(false);
  const [powerHourMultiplierSaving, setPowerHourMultiplierSaving] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState("");
  const [activeTab, setActiveTab] = useState("family");
  const isPro = familySettings?.isPro || false;
  const subscriptionStatus = familySettings?.subscriptionStatus || null;
  const subscriptionPlan = familySettings?.subscriptionPlan || null;
  const hasPaidSubscription = ["active", "trialing", "past_due"].includes(
    String(subscriptionStatus || "").toLowerCase()
  );
  const subscriptionRenewAt = familySettings?.subscriptionRenewAt?.toDate
    ? familySettings.subscriptionRenewAt.toDate()
    : familySettings?.subscriptionRenewAt
    ? new Date(familySettings.subscriptionRenewAt)
    : null;
  const complimentaryPro = Boolean(familySettings?.complimentaryPro);
  const paidPro = Boolean(familySettings?.paidPro);
  const freeKidLimit = 2;
  const freeTaskLimit = 8;
  const activeTaskCount = tasks.filter((task) => {
    const status = task.status || "open";
    return status !== "completed" && status !== "closed";
  }).length;
  const isSaving = savePending > 0;

  const createCheckoutSession = useMemo(
    () => httpsCallable(firebaseFunctions, "createStripeCheckoutSession"),
    []
  );
  const createPortalSession = useMemo(
    () => httpsCallable(firebaseFunctions, "createStripePortalSession"),
    []
  );
  const createFamilyKid = useMemo(
    () => httpsCallable(firebaseFunctions, "createFamilyKid"),
    []
  );

  useEffect(() => {
    if (!isPro) return;
    const seen = localStorage.getItem("proWelcomeSeen");
    if (!seen) setShowProWelcome(true);
  }, [isPro]);

  const addKid = async () => {
    const nextName = String(newKidName || "").trim();
    if (!nextName) {
      setNewKidError("Child name is required.");
      return;
    }
    if (!isPro && kids.length >= freeKidLimit) {
      setNewKidError(`Free plans support up to ${freeKidLimit} kids. Upgrade to add more.`);
      return;
    }
    setNewKidError("");
    try {
      await createFamilyKid({
        familyId,
        name: nextName
      });
      setNewKidName("");
      showToast({ type: "success", message: `${nextName} added.` });
    } catch (error) {
      setNewKidError(mapFirebaseError(error, "Unable to add kid."));
    }
  };

  const updateSetting = async (field, value) => {
    if (!canManage) return;
    setSaveError("");
    setSavePending((prev) => prev + 1);
    try {
      await updateDoc(doc(db, "families", familyId), { [field]: value });
      await logAudit({ familyId, action: "update", entity: "setting", meta: { field } });
      setLastSavedAt(new Date());
      setSaveNotice("Saved");
      setTimeout(() => setSaveNotice(""), 2000);
      return true;
    } catch (error) {
      setSaveError(mapFirebaseError(error, "Unable to save setting."));
      return false;
    } finally {
      setSavePending((prev) => Math.max(0, prev - 1));
    }
  };

  const handleSaveKidPin = async () => {
    if (!/^\d{4}$/.test(String(toddlerPin || ""))) {
      setToddlerPinError("Kid PIN must be exactly 4 digits.");
      return;
    }
    setToddlerPinError("");
    await updateSetting("toddlerPin", toddlerPin);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = useMemo(
    () => [
      { id: "family", label: "Family" },
      { id: "kids", label: "Kids" },
      { id: "security", label: "Security" },
      { id: "appearance", label: "Appearance" },
      { id: "schedule", label: "Schedule" },
      { id: "homeSetup", label: "Home Setup" },
      { id: "pro", label: "Pro & Dev" }
    ],
    []
  );

  const runBillingAction = async (action) => {
    try {
      setBillingLoading(true);
      setBillingStatus("");
      await action();
    } catch (error) {
      setBillingStatus(error?.message || "Billing action failed.");
    } finally {
      setBillingLoading(false);
    }
  };

  const startCheckout = async (interval) => {
    if (!familyId) return;
    await runBillingAction(async () => {
      const result = await createCheckoutSession({
        familyId,
        interval
      });
      const url = result?.data?.url;
      if (!url) throw new Error("Checkout URL was not returned.");
      window.location.assign(url);
    });
  };

  const openBillingPortal = async () => {
    if (!familyId) return;
    await runBillingAction(async () => {
      const result = await createPortalSession({ familyId });
      const url = result?.data?.url;
      if (!url) throw new Error("Billing portal URL was not returned.");
      window.location.assign(url);
    });
  };


  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const next = event?.detail;
      if (next && tabs.some(tab => tab.id === next)) {
        setActiveTab(next);
      }
    };
    window.addEventListener("tour:setSettingsTab", handler);
    return () => window.removeEventListener("tour:setSettingsTab", handler);
  }, [tabs]);



  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Family Settings</h2>
        {isPro && <span className="pro-badge">PRO</span>}
      </div>

      <div className="card-body">
        <ProWelcomeModal
          isOpen={showProWelcome}
          onClose={() => {
            localStorage.setItem("proWelcomeSeen", "true");
            setShowProWelcome(false);
          }}
        />
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {canManage && (
          <>
            <div className="settings-quick">
              <div className="settings-tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    className={`btn-nav ${activeTab === tab.id ? "active-settings-tab" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                    data-tour={
                      tab.id === "kids"
                        ? "settings-kids-tab"
                        : tab.id === "appearance"
                          ? "settings-appearance-tab"
                          : undefined
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="settings-status">
                {isSaving && <small style={{ opacity: 0.75 }}>Saving...</small>}
                {!isSaving && saveError && (
                  <small className="pin-error" role="alert">
                    {saveError}
                  </small>
                )}
                {!isSaving && !saveError && saveNotice && <span className="success-message">{saveNotice}</span>}
                {!isSaving && !saveError && !saveNotice && lastSavedAt && (
                  <small style={{ opacity: 0.7 }}>
                    Last saved {lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </small>
                )}
              </div>
            </div>

            {activeTab === "family" && (
              <>
                <div className="setting-section">
                  <h3>Family Name</h3>
                  <input
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    onBlur={() => updateSetting("familyName", familyName)}
                  />
                </div>
              </>
            )}

            {activeTab === "kids" && (
              <>
                <div className="setting-section" data-tour="settings-add-kid">
                  <h3>Add Child</h3>
                  <div className="input-group">
                    <input
                      placeholder="Child's Name"
                      value={newKidName}
                      data-tour="settings-add-kid-input"
                      onChange={e => {
                        setNewKidName(e.target.value);
                        if (newKidError) setNewKidError("");
                      }}
                    />
                    <button
                      className="btn-nav success-btn"
                      onClick={addKid}
                      data-tour="settings-add-kid-button"
                      style={{ width: "100%", marginTop: "5px" }}
                    >
                      Add Child
                    </button>
                    {newKidError && (
                      <small className="pin-error" role="alert">
                        {newKidError}
                      </small>
                    )}
                    {!isPro && (
                      <small style={{ opacity: 0.75 }}>
                        Free limit: {kids.length}/{freeKidLimit} kids.
                      </small>
                    )}
                  </div>
                </div>

                <div className="setting-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Younger Child PIN</h3>
                    <input
                      type="checkbox"
                      disabled={!isPro}
                      checked={familySettings?.toddlerLock || false}
                      onChange={(e) => updateSetting("toddlerLock", e.target.checked)}
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                    Requires a PIN for kids to mark chores as done.
                  </p>

                  {isPro && familySettings?.toddlerLock && (
                    <div className="input-group" style={{ marginTop: "10px" }}>
                      <input
                        placeholder="Custom Kid PIN (4 digits)"
                        value={toddlerPin}
                        onChange={e => {
                          setToddlerPin(e.target.value);
                          if (toddlerPinError) setToddlerPinError("");
                        }}
                      />
                      <button className="btn-nav" onClick={handleSaveKidPin}>
                        {saved ? "✅ Saved" : "Save Kid PIN"}
                      </button>
                      {toddlerPinError && (
                        <small className="pin-error" role="alert">
                          {toddlerPinError}
                        </small>
                      )}
                    </div>
                  )}
                  {!isPro && <small style={{ color: "var(--primary)" }}>⭐ Pro Feature</small>}
                </div>
              </>
            )}

            {activeTab === "security" && (
              <>
                <div className="setting-section">
                  <h3>Security PIN</h3>
                  <p style={{ fontSize: "0.75rem", opacity: 0.75 }}>
                    Default parent PIN is <strong>1234</strong>. Change it after setup.
                  </p>
                  <div className="input-group">
                    <input
                      type="password"
                      placeholder="New Parent PIN"
                      value={newPin}
                      onChange={e => setNewPin(e.target.value)}
                    />
                    <button
                      className="btn-nav"
                      onClick={() => {
                        if (!/^\d{4}$/.test(String(newPin || ""))) {
                          setPinError("Parent PIN must be exactly 4 digits.");
                          return;
                        }
                        setPinError("");
                        updateSetting("pin", newPin);
                        setNewPin("");
                      }}
                      style={{ width: "100%", marginTop: "5px" }}
                    >
                      Update PIN
                    </button>
                    {pinError && (
                      <small className="pin-error" role="alert">
                        {pinError}
                      </small>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === "appearance" && (
              <>
                <div className="setting-section">
                  <h3>Color Scheme {!isPro && "⭐"}</h3>
                  <div className="theme-grid" style={{ opacity: isPro ? 1 : 0.5, pointerEvents: isPro ? "auto" : "none" }}>
                    {["ocean", "sunset", "forest"].map(t => (
                      <button
                        key={t}
                        className={`btn-nav ${familySettings?.theme === t ? "active-theme" : ""}`}
                        onClick={() => updateSetting("theme", t)}
                      >
                        {t.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  {!isPro && <p style={{ fontSize: "0.7rem", marginTop: "5px" }}>Unlock custom themes with Pro.</p>}
                </div>

                <div className="setting-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Dark Glass {!isPro && "⭐"}</h3>
                    <input
                      type="checkbox"
                      disabled={!isPro}
                      checked={familySettings?.glassStyle === "dark"}
                      onChange={(e) => updateSetting("glassStyle", e.target.checked ? "dark" : "light")}
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                    Makes cards and panels use a darker glass finish.
                  </p>
                  {!isPro && <small style={{ color: "var(--primary)" }}>⭐ Pro Feature</small>}
                </div>

                <div className="setting-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Big Tap Mode {!isPro && "⭐"}</h3>
                    <input
                      type="checkbox"
                      disabled={!isPro}
                      checked={familySettings?.bigTapMode || false}
                      onChange={(e) => updateSetting("bigTapMode", e.target.checked)}
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                    Larger buttons and spacing for easier touch use.
                  </p>
                  {!isPro && <small style={{ color: "var(--primary)" }}>⭐ Pro Feature</small>}
                </div>

                <div className="setting-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3>Compact Mode</h3>
                    <input
                      type="checkbox"
                      checked={familySettings?.compactMode || false}
                      onChange={(e) => updateSetting("compactMode", e.target.checked)}
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                    Reduces padding and spacing for dense screens.
                  </p>
                </div>

              </>
            )}

            {activeTab === "schedule" && (
              <>
                <div className="setting-section">
                  <h3>Repeat Auto-Approve</h3>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="checkbox"
                      checked={autoApproveDailyEnabled}
                      onChange={(e) => {
                        setAutoApproveDailyEnabled(e.target.checked);
                        updateSetting("autoApproveDailyEnabled", e.target.checked);
                      }}
                    />
                    Auto-approve daily repeats at 11:55 PM
                  </label>
                  <small style={{ opacity: 0.7, display: "block", marginTop: "6px" }}>
                    Pending daily repeats auto-approve. Other repeat chores stay pending; open/failed ones clear from the board.
                  </small>
                </div>
                {isPro && (
                  <div className="setting-section">
                    <h3>Auto-Fail Rules</h3>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={autoFailEnabled}
                        onChange={(e) => {
                          setAutoFailEnabled(e.target.checked);
                          updateSetting("autoFailEnabled", e.target.checked);
                        }}
                      />
                      Enable auto-fail
                    </label>
                    <small style={{ opacity: 0.7, display: "block", marginTop: "6px" }}>
                      Grace period is in minutes. If set to 0, tasks fail right when the due time is reached.
                    </small>
                    <input
                      type="number"
                      value={autoFailGraceMins}
                      onChange={(e) => setAutoFailGraceMins(e.target.value)}
                      onBlur={() => updateSetting("autoFailGraceMins", Number(autoFailGraceMins) || 0)}
                      placeholder="Grace minutes"
                      style={{ marginTop: "8px" }}
                    />
                    <button
                      className="btn-nav"
                      type="button"
                      style={{ marginTop: "8px" }}
                      onClick={async () => {
                        setAutoFailSaving(true);
                        await updateSetting("autoFailGraceMins", Number(autoFailGraceMins) || 0);
                        setAutoFailSaving(false);
                        setAutoFailSaved(true);
                        setTimeout(() => setAutoFailSaved(false), 2000);
                      }}
                    >
                      {autoFailSaving ? "Saving..." : autoFailSaved ? "Saved ✓" : "Save grace period"}
                    </button>
                  </div>
                )}

                <div className="setting-section">
                  <h3>Clarification Messages</h3>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="checkbox"
                      checked={clarificationsEnabled}
                      onChange={(e) => {
                        setClarificationsEnabled(e.target.checked);
                        updateSetting("clarificationsEnabled", e.target.checked);
                      }}
                    />
                    Allow clarification messages
                  </label>
                  <small style={{ opacity: 0.7, display: "block", marginTop: "6px" }}>
                    Turn off to hide clarification threads and disable clarification requests.
                  </small>
                </div>

                {isPro && (
                  <div className="setting-section">
                    <h3>Power Hour</h3>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={powerHourEnabled}
                        onChange={(e) => {
                          setPowerHourEnabled(e.target.checked);
                          updateSetting("powerHourEnabled", e.target.checked);
                        }}
                      />
                      Enable Power Hour
                    </label>
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                      <input
                        type="time"
                        value={powerHourStart}
                        onChange={(e) => setPowerHourStart(e.target.value)}
                        onBlur={() => updateSetting("powerHourStart", powerHourStart)}
                      />
                      <input
                        type="time"
                        value={powerHourEnd}
                        onChange={(e) => setPowerHourEnd(e.target.value)}
                        onBlur={() => updateSetting("powerHourEnd", powerHourEnd)}
                      />
                    </div>
                    <button
                      className="btn-nav"
                      type="button"
                      style={{ marginTop: "8px" }}
                      onClick={async () => {
                        setPowerHourSaving(true);
                        await updateSetting("powerHourStart", powerHourStart);
                        await updateSetting("powerHourEnd", powerHourEnd);
                        setPowerHourSaving(false);
                        setPowerHourSaved(true);
                        setTimeout(() => setPowerHourSaved(false), 2000);
                      }}
                    >
                      {powerHourSaving ? "Saving..." : powerHourSaved ? "Saved ✓" : "Save Power Hour"}
                    </button>
                    <input
                      type="number"
                      step="0.1"
                      value={powerHourMultiplier}
                      onChange={(e) => setPowerHourMultiplier(e.target.value)}
                      onBlur={() => updateSetting("powerHourMultiplier", Number(powerHourMultiplier) || 1.5)}
                      placeholder="Multiplier"
                      style={{ marginTop: "8px" }}
                    />
                    <button
                      className="btn-nav"
                      type="button"
                      style={{ marginTop: "8px" }}
                      onClick={async () => {
                        setPowerHourMultiplierSaving(true);
                        await updateSetting("powerHourMultiplier", Number(powerHourMultiplier) || 1.5);
                        setPowerHourMultiplierSaving(false);
                        setPowerHourMultiplierSaved(true);
                        setTimeout(() => setPowerHourMultiplierSaved(false), 2000);
                      }}
                    >
                      {powerHourMultiplierSaving ? "Saving..." : powerHourMultiplierSaved ? "Saved ✓" : "Save multiplier"}
                    </button>
                  </div>
                )}

                {!isPro && (
                  <div className="setting-section">
                    <p style={{ opacity: 0.7 }}>Upgrade to Pro to unlock Power Hour and Auto-Fail.</p>
                  </div>
                )}
              </>
            )}

            {activeTab === "pro" && (
              <>
                {!isPro && (
                  <div className="setting-section">
                    <h3>Free vs Pro</h3>
                    <p style={{ opacity: 0.75 }}>
                      Billing is per family. Upgrade this family to unlock sharing and premium tools.
                    </p>
                    <div className="card" style={{ marginBottom: 0 }}>
                      <div style={{ display: "grid", gap: 8 }}>
                        <div><strong>Free:</strong> up to 2 kids and 8 active chores</div>
                        <div><strong>Pro:</strong> unlimited kids/chores, family sharing, templates, integrations, analytics</div>
                        <div style={{ opacity: 0.75 }}>
                          Current usage: {kids.length}/{freeKidLimit} kids, {activeTaskCount}/{freeTaskLimit} active chores
                        </div>
                        {(kids.length >= freeKidLimit || activeTaskCount >= freeTaskLimit) && (
                          <small className="pin-error" role="alert">
                            You are at the free plan limit. Upgrade to keep growing.
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="setting-section">
                  <h3>Pro Level</h3>
                  <p style={{ opacity: 0.75 }}>
                    Pro access is managed by billing and admin overrides. Family members cannot self-enable Pro.
                  </p>
                  <p style={{ opacity: 0.75, marginTop: "6px" }}>
                    Status: {subscriptionStatus || (isPro ? "active" : "inactive")}
                    {subscriptionPlan ? ` · ${subscriptionPlan}` : ""}
                    {subscriptionRenewAt ? ` · renews ${subscriptionRenewAt.toLocaleDateString()}` : ""}
                  </p>
                  {complimentaryPro && (
                    <small style={{ opacity: 0.75, display: "block", marginTop: "4px" }}>
                      Complimentary Pro is enabled by admin override for this family.
                    </small>
                  )}
                  {paidPro && (
                    <small style={{ opacity: 0.75, display: "block", marginTop: "4px" }}>
                      Paid subscription is active for this family.
                    </small>
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", opacity: 0.8 }}>
                    <input type="checkbox" checked={isPro} readOnly />
                    Pro currently {isPro ? "enabled" : "disabled"} for this family
                  </label>
                  <small style={{ opacity: 0.7 }}>
                    If this family should have complimentary access, an admin can set it in the Admin panel.
                  </small>
                  <p style={{ opacity: 0.75, marginTop: "8px" }}>
                    Price: <strong>$3/month</strong> or <strong>$30/year</strong>. Includes a <strong>7-day free trial</strong> at checkout.
                  </p>
                  <p style={{ opacity: 0.75, marginTop: "4px" }}>
                    Billing upgrades this family only, so each family has its own plan.
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
                    {!complimentaryPro && (
                      <>
                        <button
                          className="btn-nav"
                          type="button"
                          disabled={billingLoading}
                          onClick={() => startCheckout("monthly")}
                        >
                          Start $3/month
                        </button>
                        <button
                          className="btn-nav"
                          type="button"
                          disabled={billingLoading}
                          onClick={() => startCheckout("yearly")}
                        >
                          Start $30/year
                        </button>
                      </>
                    )}
                    {hasPaidSubscription && (
                      <button
                        className="btn-nav"
                        type="button"
                        disabled={billingLoading}
                        onClick={openBillingPortal}
                      >
                        Manage subscription
                      </button>
                    )}
                  </div>
                  {billingStatus && (
                    <small style={{ opacity: 0.75, display: "block", marginTop: "6px" }}>
                      {billingStatus}
                    </small>
                  )}
                </div>
              </>
            )}

            {activeTab === "homeSetup" && (
              <div className="setting-section" data-tour="settings-home-setup">
                <h3>Home Setup</h3>
                <p style={{ opacity: 0.75 }}>
                  Suggested setup for tablets + kid devices:
                </p>
                <ul style={{ margin: 0, paddingLeft: "18px", display: "grid", gap: "6px" }}>
                  <li>Put a shared tablet in a central place (kitchen or family room).</li>
                  <li>Enable Big Tap Mode for touch use.</li>
                  <li>Use Kids Mode on the shared screen.</li>
                  <li>Install the app on each kid’s device if they have one.</li>
                  <li>Turn on Compact Mode for dense screens.</li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
